import { Component } from "react";
import Parse from "parse";
import produce from "immer";
import { Message } from "src/modules/communication-protobuf";
import DeviceDiscoveryProvider from "src/modules/device-discovery/Provider";
import PairingRequester from "src/modules/pairing-requester/PairingRequester";
import PairingResponder from "src/modules/pairing-responder/PairingResponder";
import PairingWizard from "src/modules/pairing/Wizard";
import PurchaseProvider from "src/modules/purchase/PurchaseContext";
import * as authCloud from "src/modules/auth/ajax/cloud";
import SplashScreen from "src/scenes/SplashScreen";
import AppFailure from "src/scenes/AppFailure";
import AlreadyOpen from "src/scenes/AlreadyOpen";
import * as actions from "src/duck/actions";
import { startApp } from "src/duck/epics";
import AppStatus from "src/constants/AppStatus";
import Presence from "src/constants/presence";
import MessengerProvider, { initializeMessenger } from "src/modules/messenger/Provider";
import createCloudSynchronizer from "src/modules/cloud-synchronizer";
import Frame from "src/components/Frame";
import { getStatus, getPresence } from "src/duck/selectors";
import { store } from "src/store/store";
import environment from "src/utils/environment";
import { ActivityService } from "src/modules/messenger/lib/ActivityService";
import { signOutFromWeb, redirectToWebLogin } from "src/browser/browserCredentials";
import appUpdater, { UpdateNotifier } from "src/modules/web-updater";
import withRouter from "src/routing/withRouter";
import createPermissions from "./createPermissions";
import MicrophonePermissionDialog from "./components/MicrophonePermissionDialog";
import CameraPermissionDialog from "./components/CameraPermissionDialog";
import ConnectionLostDialog from "./components/ConnectionLostDialog";
import BeforeAppCloseDialog from "./components/BeforeAppCloseDialog";
import AppContext from "./Context";
import EventEmitter from "events";

const log = window.require("electron-log");
const remote = window.require("@electron/remote");
const { isBrowser } = environment;
const activityService = new ActivityService();
appUpdater.create();
const emitter = new EventEmitter();

class App extends Component {
  constructor(props) {
    super(props);
    this.permissions = createPermissions();
    this.permissions.openDialog = (mediaType) => {
      const permissionDialogKey =
        mediaType === "microphone" ? "isOpenedMicrophonePermissionDialog" : "isOpenedCameraPermissionDialog";
      this.setState({ [permissionDialogKey]: true });
    };
    this.state = {
      messenger: null,
      isOpenedMicrophonePermissionDialog: false,
      isOpenedCameraPermissionDialog: false,
      providing: {
        permissions: this.permissions,
        reset: this.handleAppReset,
        restart: this.handleAppRestart,
        synchronizer: createCloudSynchronizer({
          dispatch: this.props.dispatch
        }),
        emitter
      }
    };
  }

  componentDidMount() {
    const { dispatch } = this.props;
    if (isBrowser && window.appAlreadyOpen) {
      dispatch(actions.appAlreadyOpen());
      return;
    }

    window.addEventListener("offline", this.connectionLostHandler);

    if (navigator.onLine) this.handleAppStart();
    else dispatch(actions.appFailure());

    if (environment.debugMode === false) {
      this.setUpUserActivityHandlers();
    }
  }

  componentDidUpdate(prevProps, prevState) {
    const { status } = this.props;

    if (prevProps.status === AppStatus.STARTED && status === AppStatus.CONNECTION_LOST) {
      this.connectionReestablisher();
    }
  }

  enterInactiveMode = async () => {
    const { messenger } = this.state;
    const { dispatch } = this.props;
    try {
      await messenger.stop();
      dispatch(actions.enterInactiveMode());
    } catch (error) {
      log.error("enterInactiveMode failed", error);
    }
  };

  setUpUserActivityHandlers = () => {
    if (isBrowser) return;
    const browserWindow = remote.getCurrentWindow();
    let enterInactiveModeTimeoutId = null;
    const handleBlur = () => {
      const status = getStatus(store.getState());
      const presence = getPresence(store.getState());
      if (
        status !== AppStatus.STARTED ||
        presence === Presence.RECEIVER_CONNECTED ||
        presence === Presence.SENDER_CONNECTED ||
        presence === Presence.SENDER
      ) {
        return;
      }
      enterInactiveModeTimeoutId = setTimeout(
        () => {
          this.enterInactiveMode();
        },
        5 * 60 * 1000
      );
    };
    const handleFocus = () => {
      clearTimeout(enterInactiveModeTimeoutId);
      if (getStatus(store.getState()) === AppStatus.INACTIVE_MODE) {
        this.handleAppRestart();
      }
    };
    browserWindow.on("blur", handleBlur);
    browserWindow.on("focus", handleFocus);
  };

  connectionLostHandler = () => {
    const { status, dispatch } = this.props;

    if (status === AppStatus.STARTED) {
      dispatch(actions.connectionLost());
    }
  };

  connectionReestablisher = async () => {
    const { messenger } = this.state;
    try {
      await messenger.stop();
    } catch (error) {
      log.error(error);
    }

    if (navigator.onLine) {
      this.reconnect();
    } else {
      window.addEventListener("online", this.reconnect, {
        once: true
      });
    }
  };

  reconnect = async () => {
    const { messenger } = this.state;
    const { dispatch, status } = this.props;

    if (status === AppStatus.FAILURE) {
      return;
    }

    try {
      await messenger.restart();
      dispatch(actions.connectionReestablished());
    } catch (error) {
      this.reconnectTimeoutId = setTimeout(this.reconnect, 5000);
    }
  };

  handleDeviceUnpairedMessage = async () => {
    try {
      await Parse.Session.current();
    } catch (error) {
      this.handleAppReset();
    }
  };

  handleAppStart = () => {
    const { synchronizer } = this.state.providing;
    const { dispatch, router } = this.props;

    if (this.state.messenger) {
      this.state.messenger.off(Message.DeviceUnpairedMessage.name, this.handleDeviceUnpairedMessage);
    }
    const messenger = dispatch(initializeMessenger);

    messenger.on(Message.DeviceUnpairedMessage.name, this.handleDeviceUnpairedMessage);

    synchronizer.setMessenger(messenger);

    this.setState(
      produce((prevState) => {
        prevState.messenger = messenger;
      }),
      () => {
        dispatch(
          startApp({
            navigate: router.navigate,
            messenger,
            synchronizer,
            activityService,
            handleAppReset: this.handleAppReset
          })
        );
      }
    );
  };

  handleAppRestart = async () => {
    const { dispatch } = this.props;
    dispatch(actions.appRestart());
    this.handleAppStart();
  };

  handleAppReset = async () => {
    const { synchronizer, activityLogManager } = this.state.providing;
    const { dispatch, persistor, xmppLogin } = this.props;
    dispatch(actions.appReset());
    try {
      if (isBrowser) await signOutFromWeb(xmppLogin);
      else await authCloud.signOut(xmppLogin);
      await Parse.User.logOut();
    } catch (error) {
      log.error(error);
    }

    try {
      if (!isBrowser) {
        await remote.getCurrentWebContents().session.clearStorageData();
      }
      if (isBrowser) window.localStorage.clear();

      await persistor.flush();
      await persistor.purge();
      if (activityLogManager) await activityLogManager.clearData();
      await synchronizer.clearData();
    } catch (error) {
      log.error(error);
    }
    if (!isBrowser) {
      remote.app.relaunch();
      remote.app.quit();
    }
    if (isBrowser) redirectToWebLogin();
  };

  dismissConnectionLostDialog = () => {
    clearTimeout(this.reconnectTimeoutId);
    this.handleAppRestart();
  };

  dismissMicrophonePermissionDialog = () => this.setState({ isOpenedMicrophonePermissionDialog: false });
  onOpenMicrophonePermissionsSettings = () => {
    this.permissions.openSettings("microphone");
    this.dismissMicrophonePermissionDialog();
  };

  dismissCameraPermissionDialog = () => this.setState({ isOpenedCameraPermissionDialog: false });
  onOpenCameraPermissionsSettings = () => {
    this.permissions.openSettings("camera");
    this.dismissCameraPermissionDialog();
  };

  render() {
    const { providing, messenger, isOpenedCameraPermissionDialog, isOpenedMicrophonePermissionDialog } = this.state;
    let { status, presence } = this.props;
    let toRender = null;

    const renderConnectionLostDialog = status === AppStatus.CONNECTION_LOST && presence !== Presence.RECEIVER_CONNECTED;

    if (status === AppStatus.FAILURE) {
      toRender = <AppFailure onRestart={this.handleAppRestart} />;
    }

    if (status === AppStatus.ALREADY_OPEN) {
      toRender = <AlreadyOpen />;
    }

    if (status === AppStatus.STARTING || status === AppStatus.SIGNING_OUT || status === AppStatus.INACTIVE_MODE) {
      toRender = <SplashScreen status={status} />;
    }

    if (status === AppStatus.STARTED || status === AppStatus.CONNECTION_LOST) {
      toRender = (
        <AppContext.Provider value={providing}>
          <MessengerProvider messenger={messenger}>
            <DeviceDiscoveryProvider>
              <PurchaseProvider>
                <PairingRequester>
                  <PairingResponder>
                    <UpdateNotifier />
                    <PairingWizard />
                    <ConnectionLostDialog
                      isOpen={renderConnectionLostDialog}
                      onDismiss={this.dismissConnectionLostDialog}
                    />
                    <MicrophonePermissionDialog
                      isOpen={isOpenedMicrophonePermissionDialog}
                      onDismiss={this.dismissMicrophonePermissionDialog}
                      openSettings={this.onOpenMicrophonePermissionsSettings}
                    />
                    <CameraPermissionDialog
                      isOpen={isOpenedCameraPermissionDialog}
                      onDismiss={this.dismissCameraPermissionDialog}
                      openSettings={this.onOpenCameraPermissionsSettings}
                      emitter={emitter}
                    />
                    <BeforeAppCloseDialog />
                    {this.props.children}
                  </PairingResponder>
                </PairingRequester>
              </PurchaseProvider>
            </DeviceDiscoveryProvider>
          </MessengerProvider>
        </AppContext.Provider>
      );
    }

    return <Frame>{toRender}</Frame>;
  }
}

export default withRouter(App);
